import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, AtSign, ChevronDown, Download, FileUp, Flag, Inbox, Mail, MailOpen, Paperclip, Pin, RefreshCw, Search, Star, X } from "lucide-react";
import type { Email, Address } from "postal-mime";
import type { InboxMessage, InboxResponse, MailboxesResponse, MailCategory } from "../shared/types";
import { refreshWhileVisible } from "./autoRefresh";
import { mailDocument } from "./mailDocument";
import { detectMailLanguage, localTranslator, translateMailHtml, translateMailText } from "./mailTranslation";
import { BilingualTitle } from "./BilingualTitle";
import { titlePair } from "./titleTranslation";
import "./inbox.css";

const EMPTY_BOXES: MailboxesResponse = { mailboxes: [], total: 0, messages: 0, unread: 0 };
export const messageTitle = (message: InboxMessage) => message.subject || (message.contentStatus === "legacy" ? "旧记录 · 未保存标题" : "（无主题）");
const dateTime = (value: string) => new Date(value).toLocaleString("zh-CN", { hour12: false });
const sizeText = (value: number) => value < 1024 ? value + " B" : value < 1048576 ? (value / 1024).toFixed(1) + " KB" : (value / 1048576).toFixed(1) + " MB";
const categoryLabel: Record<MailCategory, string> = { attention: "需关注", promotion: "广告", normal: "普通" };
function addresses(values?: Address[]) {
  return (values ?? []).flatMap(value => value.group ?? [value]).map(value => value.name ? value.name + " <" + value.address + ">" : value.address).join("、");
}
async function privateFetch(url: string, init?: RequestInit) {
  const response = await fetch(url, { ...init, credentials: "same-origin" });
  if (response.status === 401) {
    window.dispatchEvent(new Event("mail-auth-expired"));
    throw new Error("登录已过期，请重新登录。");
  }
  return response;
}
async function getJson<T>(url: string, signal: AbortSignal): Promise<T> {
  const response = await privateFetch(url, { signal });
  if (!response.ok) throw new Error("无法加载邮件，请检查网络后重试。");
  return response.json();
}

function keepDialogFocus(event: React.KeyboardEvent<HTMLDialogElement>) {
  if (event.key !== "Tab" || !(event.target instanceof Element) || event.target.closest("dialog") !== event.currentTarget) return;
  const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
    'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),iframe,[tabindex]:not([tabindex="-1"])',
  )).filter(element => element.getClientRects().length > 0);
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement as HTMLElement))) {
    event.preventDefault(); last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault(); first?.focus();
  }
}

function ImportDialog({ recipient, onClose, onImported }: { recipient: string; onClose: () => void; onImported: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [address, setAddress] = useState(recipient);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  useLayoutEffect(() => {
    const opener = document.activeElement;
    const element = ref.current;
    element?.showModal();
    return () => {
      element?.close();
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy || !files.length) return;
    setBusy(true); setNotice("");
    let imported = 0;
    try {
      for (const file of files) {
        if (file.size > 30 * 1024 * 1024 || !file.size) throw new Error(file.name + "：请选择不超过 30 MB 的 EML 文件");
        const response = await privateFetch("/api/messages/import?recipient=" + encodeURIComponent(address.trim()), {
          method: "POST", headers: { "Content-Type": "message/rfc822" }, body: file, credentials: "same-origin",
        });
        const result = await response.json() as { error?: string };
        if (!response.ok) throw new Error(result.error ?? "导入失败，请重试");
        imported++;
      }
      setNotice("已处理 " + imported + " 封邮件；相同 Message-ID、发件人与收件地址的邮件会合并。");
      setFiles([]); onImported();
    } catch (error) { setNotice("已处理 " + imported + " 封。" + (error instanceof Error ? error.message : "导入失败，请重试。")); }
    finally { setBusy(false); }
  }
  return (
    <dialog ref={ref} className="import-dialog" aria-labelledby="import-dialog-title" onKeyDown={keepDialogFocus} onCancel={event => { event.preventDefault(); event.stopPropagation(); if (!busy) onClose(); }}>
      <div className="reader-toolbar"><h2 id="import-dialog-title">导入历史邮件</h2><button className="icon-button" aria-label="关闭导入窗口" onClick={onClose} disabled={busy}><X aria-hidden="true" /></button></div>
      <p>在 Gmail 中打开邮件，选择「更多 → 下载邮件」，再导入 EML 文件。文件只保存到你的私有收件箱，不会重新发送。</p>
      <form onSubmit={event => void submit(event)}>
        <label htmlFor="import-recipient">归入收件地址</label>
        <input id="import-recipient" type="email" value={address} onChange={e => setAddress(e.target.value)} placeholder="例如 test@zzjmail.com" required disabled={busy} />
        <label htmlFor="import-files">选择邮件文件（可多选）</label>
        <input id="import-files" type="file" accept=".eml,message/rfc822" multiple onChange={e => setFiles(Array.from(e.target.files ?? []))} disabled={busy} />
        {notice && <p className="import-notice" role="status">{notice}</p>}
        <button className="button button--primary" disabled={busy || files.length === 0}>{busy ? "正在导入…" : "导入邮件"}</button>
      </form>
    </dialog>
  );
}

type ParsedMail = { email: Email; rawSize: number; attachments: { name: string; url: string; size: number; contentId?: string; inlineUrl?: string }[] };
function imageDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("内嵌图片读取失败，可下载原文查看。"));
    reader.readAsDataURL(blob);
  });
}

type MailStateField = "starred" | "pinned" | "important" | "archived" | "trashed";

async function copyMailText(value: string) {
  if (!value) throw new Error("没有可复制的内容。");
  try { await navigator.clipboard.writeText(value); return; } catch { /* The embedded page may not have clipboard permission. */ }
  const input = document.createElement("textarea");
  input.value = value; input.style.position = "fixed"; input.style.opacity = "0";
  document.body.appendChild(input); input.select();
  const copied = document.execCommand("copy"); input.remove();
  if (!copied) throw new Error("复制失败，请手动选中文字复制。");
}

function Reader({ message, onClose, onMessageChange, onImport, onSearch }: {
  message: InboxMessage; onClose: () => void; onMessageChange: (message: InboxMessage) => void; onImport: () => void;
  onSearch?: (value: string) => void;
}) {
  const [parsed, setParsed] = useState<ParsedMail | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(message.contentStatus === "stored");
  const [plain, setPlain] = useState(false);
  const [retry, setRetry] = useState(0);
  const [readBusy, setReadBusy] = useState(false);
  const [readError, setReadError] = useState("");
  const [actionNotice, setActionNotice] = useState("");
  const actions = useRef<HTMLDetailsElement>(null);
  const [imageStatus, setImageStatus] = useState({ total: 0, pending: 0, failed: 0 });
  const [sourceLanguage, setSourceLanguage] = useState("en");
  const [translation, setTranslation] = useState<{ mode: "html" | "plain"; subject: string; body: string } | null>(null);
  const [translationStatus, setTranslationStatus] = useState("");
  const [translating, setTranslating] = useState(false);
  const translationRun = useRef(0);
  const translationController = useRef<AbortController | null>(null);
  const sourceLanguageTouched = useRef(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const bodyFrame = useRef<HTMLIFrameElement>(null);
  useEffect(() => { heading.current?.focus({ preventScroll: true }); if (window.matchMedia("(max-width: 760px)").matches) heading.current?.scrollIntoView({ block: "start" }); }, [message.id]);
  useEffect(() => {
    if (message.contentStatus !== "stored") return;
    const controller = new AbortController();
    const urls: string[] = [];
    let worker: Worker | undefined;
    setLoading(true); setError(""); setParsed(null);
    void (async () => {
      try {
        const response = await privateFetch("/api/messages/" + message.id + "/raw", { signal: controller.signal });
        if (!response.ok) {
          const payload: { error?: string } = await response.json();
          throw new Error(payload.error ?? "无法读取原文，请重试。");
        }
        const bytes = await response.arrayBuffer();
        const rawSize = bytes.byteLength;
        if (controller.signal.aborted) return;
        worker = new Worker(new URL("./mime.worker.ts", import.meta.url), { type: "module" });
        const email = await new Promise<Email>((resolve, reject) => {
          worker!.onmessage = (event: MessageEvent<{ email?: Email; error?: string }>) => event.data.email ? resolve(event.data.email) : reject(new Error(event.data.error ?? "解析失败"));
          worker!.onerror = () => reject(new Error("邮件解析失败，可下载原文查看。"));
          worker!.postMessage(bytes, [bytes]);
        });
        worker.terminate();
        if (controller.signal.aborted) return;
        const attachments = await Promise.all(email.attachments.map(async (attachment, index) => {
          const content = attachment.content instanceof Uint8Array ? Uint8Array.from(attachment.content).buffer : attachment.content;
          const safeImage = /^image\/(png|jpeg|gif|webp|avif|bmp|x-icon|vnd.microsoft.icon|svg\+xml)$/i.test(attachment.mimeType);
          const blob = new Blob([content], { type: safeImage ? attachment.mimeType : "application/octet-stream" });
          const url = URL.createObjectURL(blob); urls.push(url);
          const inlineUrl = safeImage && attachment.contentId ? await imageDataUrl(blob) : undefined;
          return { name: attachment.filename || "附件 " + (index + 1), url, size: blob.size, contentId: attachment.contentId, inlineUrl };
        }));
        if (controller.signal.aborted) return;
        setParsed({ email, rawSize, attachments }); setPlain(!email.html);
      } catch (failure) { if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : "读取失败，请重试。"); }
      finally { if (!controller.signal.aborted) setLoading(false); }
    })();
    return () => { controller.abort(); worker?.terminate(); urls.forEach(url => URL.revokeObjectURL(url)); };
  }, [message.id, message.contentStatus, retry]);

  const safeDocument = useMemo(() => parsed?.email.html ? mailDocument(parsed.email.html, parsed.attachments) : null, [parsed]);
  useEffect(() => {
    let active = true;
    sourceLanguageTouched.current = false;
    translationRun.current++;
    translationController.current?.abort();
    translationController.current = null;
    setTranslation(null);
    setTranslationStatus("");
    setTranslating(false);
    const declared = safeDocument?.language.split("-")[0].toLowerCase();
    const fallback = ["en", "ja", "ko", "fr", "de", "es", "zh"].includes(declared ?? "") ? declared! : "en";
    setSourceLanguage(fallback);
    const sample = parsed?.email.text || (safeDocument ? new DOMParser().parseFromString(safeDocument.document, "text/html").body.textContent || "" : "");
    void detectMailLanguage(sample).then(detected => { if (active && detected && !sourceLanguageTouched.current) setSourceLanguage(detected); });
    return () => { active = false; translationRun.current++; translationController.current?.abort(); };
  }, [message.id, parsed, safeDocument]);
  function cancelTranslation() {
    translationRun.current++;
    translationController.current?.abort();
    translationController.current = null;
    setTranslation(null);
    setTranslationStatus("");
    setTranslating(false);
  }
  async function translateVisible() {
    if (translating) { cancelTranslation(); return; }
    if (translation) { cancelTranslation(); return; }
    if (sourceLanguage === "zh") { setTranslationStatus("检测到正文已是中文，可直接阅读原文；如检测有误，请手动选择原文语言。"); return; }
    const factory = localTranslator();
    if (!factory) {
      setTranslationStatus("此 Chrome 尚不支持本机翻译，请更新桌面 Chrome，或使用浏览器的翻译功能。");
      return;
    }
    const controller = new AbortController();
    const run = ++translationRun.current;
    translationController.current = controller;
    setTranslating(true);
    setTranslationStatus("正在准备本机翻译…首次使用可能需要下载语言包。");
    let translator: Awaited<ReturnType<typeof factory.create>> | undefined;
    try {
      // create() must run in this click's user activation, before the first await.
      translator = await factory.create({ sourceLanguage, targetLanguage: "zh", signal: controller.signal });
      if (controller.signal.aborted) return;
      const originalSubject = parsed?.email.subject || messageTitle(message);
      const subject = sourceLanguage === "en" && titlePair(originalSubject) === "en-zh"
        ? await translateMailText(originalSubject, translator, controller.signal) : originalSubject;
      const mode = plain ? "plain" : "html";
      const body = mode === "plain"
        ? await translateMailText(parsed?.email.text || "", translator, controller.signal)
        : await translateMailHtml(safeDocument?.document || "", translator, controller.signal, (done, total) => {
          if (run === translationRun.current) setTranslationStatus(`正在翻译正文 ${done}/${total}…`);
        });
      if (run === translationRun.current && !controller.signal.aborted) {
        setTranslation({ mode, subject, body });
        setTranslationStatus("已在此设备翻译；可随时查看原文。");
      }
    } catch (failure) {
      if (run === translationRun.current && !controller.signal.aborted) {
        setTranslationStatus("本机翻译失败。请检查 Chrome 语言包和网络后重试；原文仍可阅读。" + (failure instanceof Error && failure.name === "NotSupportedError" ? "此语言组合暂不受支持。" : ""));
      }
    } finally {
      translator?.destroy();
      if (run === translationRun.current) {
        translationController.current = null;
        setTranslating(false);
      }
    }
  }
  const showTranslation = translation?.mode === (plain ? "plain" : "html") ? translation : null;
  useEffect(() => {
    const frame = bodyFrame.current;
    if (!frame || plain || !safeDocument) return;
    let observer: ResizeObserver | undefined;
    let pending = 0;
    let connecting = 0;
    let observedBody: HTMLElement | null = null;
    let detach = () => {};
    setImageStatus({ total: 0, pending: 0, failed: 0 });
    const connect = () => {
      const body = frame.contentDocument?.body;
      if (!body?.querySelector(".mail-document")) {
        cancelAnimationFrame(connecting); connecting = requestAnimationFrame(connect); return;
      }
      if (body === observedBody) return;
      observedBody = body;
      observer?.disconnect(); detach();
      const measure = () => {
        cancelAnimationFrame(pending);
        pending = requestAnimationFrame(() => {
          const height = Math.min(200000, Math.max(160, Math.ceil(body.getBoundingClientRect().height)));
          if (frame.style.height !== height + "px") frame.style.height = height + "px";
          const images = Array.from(body.querySelectorAll("img")).filter(image => {
            if (image.hasAttribute("width") && image.hasAttribute("height") && (Number(image.getAttribute("width")) <= 1 || Number(image.getAttribute("height")) <= 1)) return false;
            const box = image.getBoundingClientRect(); return box.width > 1 || box.height > 1;
          });
          const status = { total: images.length, pending: images.filter(image => !image.complete).length, failed: images.filter(image => image.complete && !image.naturalWidth).length };
          setImageStatus(previous => previous.total === status.total && previous.pending === status.pending && previous.failed === status.failed ? previous : status);
        });
      };
      body.addEventListener("load", measure, true); body.addEventListener("error", measure, true);
      detach = () => { body.removeEventListener("load", measure, true); body.removeEventListener("error", measure, true); };
      observer = new ResizeObserver(measure); observer.observe(body); measure();
    };
    frame.addEventListener("load", connect);
    connect();
    return () => { frame.removeEventListener("load", connect); observer?.disconnect(); detach(); cancelAnimationFrame(pending); cancelAnimationFrame(connecting); };
  }, [safeDocument, plain, showTranslation]);

  async function toggleRead() {
    setReadBusy(true); setReadError("");
    try {
      const response = await privateFetch("/api/messages/" + message.id + "/read?value=" + (message.isRead ? "0" : "1"), { method: "PUT" });
      if (!response.ok) throw new Error();
      onMessageChange({ ...message, isRead: !message.isRead });
    } catch { setReadError("未能更新已读状态，请重试。"); }
    finally { setReadBusy(false); }
  }
  async function setState(field: MailStateField, value: boolean) {
    if (readBusy) return;
    setReadBusy(true); setReadError(""); setActionNotice("");
    try {
      const response = await privateFetch("/api/messages/" + message.id + "/state", {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ field, value }),
      });
      if (!response.ok) throw new Error();
      onMessageChange(await response.json() as InboxMessage);
      actions.current?.removeAttribute("open");
      setActionNotice(({ starred: "星标", pinned: "置顶", important: "重要标记", archived: "归档", trashed: "回收站" })[field] + (value ? "已设置。" : "已取消。"));
    } catch { setReadError("邮件操作失败，请检查网络后重试。"); }
    finally { setReadBusy(false); }
  }
  async function setCategory(category: MailCategory | null) {
    if (readBusy) return;
    setReadBusy(true); setReadError(""); setActionNotice("");
    try {
      const response = await privateFetch("/api/messages/" + message.id + "/category", {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ category }),
      });
      if (!response.ok) throw new Error();
      onMessageChange(await response.json() as InboxMessage);
      actions.current?.removeAttribute("open");
      setActionNotice(category ? "已改为“" + categoryLabel[category] + "”。" : "已恢复自动分类。");
    } catch { setReadError("分类更新失败，请检查网络后重试。"); }
    finally { setReadBusy(false); }
  }
  async function copy(value: string, label: string) {
    setReadError("");
    try { await copyMailText(value); setActionNotice(label + "已复制。"); actions.current?.removeAttribute("open"); }
    catch (failure) { setReadError(failure instanceof Error ? failure.message : "复制失败。"); }
  }
  return (
    <section className="mail-reader" aria-label="邮件阅读区">
      <div className="reader-toolbar">
        <button className="button button--plain" onClick={onClose}><ArrowLeft aria-hidden="true" />返回列表</button>
        <button className="icon-button" aria-label={message.isRead ? "标为未读" : "标为已读"} disabled={readBusy} onClick={() => void toggleRead()}>{message.isRead ? <Mail aria-hidden="true" /> : <MailOpen aria-hidden="true" />}</button>
        <button className="icon-button" aria-label={message.isStarred ? "取消星标" : "加星标"} aria-pressed={message.isStarred} disabled={readBusy} onClick={() => void setState("starred", !message.isStarred)}><Star aria-hidden="true" /></button>
        <button className="icon-button" aria-label={message.isPinned ? "取消置顶" : "置顶邮件"} aria-pressed={message.isPinned} disabled={readBusy} onClick={() => void setState("pinned", !message.isPinned)}><Pin aria-hidden="true" /></button>
        <details className="mail-actions" ref={actions}><summary>操作</summary><div className="mail-actions-grid">
          <button disabled={readBusy} onClick={() => void setState("important", !message.isImportant)}>{message.isImportant ? "取消重要标记" : "标为重要"}</button>
          <button disabled={readBusy} onClick={() => void setCategory("attention")}>归为需关注</button>
          <button disabled={readBusy} onClick={() => void setCategory("promotion")}>归为广告</button>
          <button disabled={readBusy} onClick={() => void setCategory("normal")}>归为普通</button>
          {message.categorySource === "manual" && <button disabled={readBusy} onClick={() => void setCategory(null)}>恢复自动分类</button>}
          <button disabled={readBusy} onClick={() => void setState("archived", !message.isArchived)}>{message.isArchived ? "移回收件箱" : "归档邮件"}</button>
          <button disabled={readBusy} onClick={() => void setState("trashed", !message.isTrashed)}>{message.isTrashed ? "从回收站恢复" : "移入回收站"}</button>
          <button onClick={() => void copy(message.sender, "发件人")}>复制发件人</button>
          <button onClick={() => void copy(message.recipient, "收件地址")}>复制收件地址</button>
          <button onClick={() => void copy(parsed?.email.subject || messageTitle(message), "主题")}>复制主题</button>
          <button disabled={!parsed?.email.messageId} onClick={() => void copy(parsed?.email.messageId || "", "Message-ID")}>复制 Message-ID</button>
          <button disabled={!parsed} onClick={() => void copy(parsed?.email.text || bodyFrame.current?.contentDocument?.body?.innerText || "", "正文")}>复制正文</button>
          {onSearch && <button onClick={() => onSearch(message.sender)}>查找同一发件人</button>}
          {onSearch && <button disabled={!message.subject} onClick={() => onSearch(message.subject)}>查找相同主题</button>}
        </div></details>
        {message.contentStatus === "stored" && <a className="button" href={"/api/messages/" + message.id + "/raw"} download><Download aria-hidden="true" />原文</a>}
      </div>
      <div className="mail-detail-head">
        <h2 ref={heading} tabIndex={-1}><BilingualTitle title={parsed?.email.subject || messageTitle(message)} provided={showTranslation?.subject} /></h2>
        <dl>
          <div><dt>发件人</dt><dd>{message.senderName ? message.senderName + " <" + message.senderAddress + ">" : parsed?.email.from ? addresses([parsed.email.from]) : message.senderAddress}</dd></div>
          <div><dt>收件人</dt><dd>{message.recipientName ? message.recipientName + " <" + message.recipient + ">" : message.recipient}</dd></div>
          <div><dt>收件时间</dt><dd>{dateTime(message.receivedAt)}</dd></div>
          <div><dt>邮件类型</dt><dd><span className={"mail-category mail-category--" + message.category}>{categoryLabel[message.category]}</span><span className="mail-category-note">{message.categorySource === "manual" ? "手动指定" : message.categorySource === "important" ? "因重要标记归为需关注" : "根据主题与邮件头自动判断，可在“操作”中修正"}</span></dd></div>
          <div><dt>转发至</dt><dd>{message.forwardedTo}<span className={"delivery-text delivery-text--" + message.status}>{message.source === "imported" ? "历史导入 · 未核实转发结果" : message.status === "forwarded" ? "已转发" : message.status === "failed" ? "转发失败" : "处理中"}</span></dd></div>
        </dl>
        {parsed && <details className="mail-headers"><summary>完整邮件信息</summary><dl>
          <div><dt>邮件 To</dt><dd>{addresses(parsed.email.to) || "未提供"}</dd></div>
          {parsed.email.cc?.length ? <div><dt>抄送</dt><dd>{addresses(parsed.email.cc)}</dd></div> : null}
          {parsed.email.replyTo?.length ? <div><dt>回复地址</dt><dd>{addresses(parsed.email.replyTo)}</dd></div> : null}
          <div><dt>发送时间</dt><dd>{parsed.email.date || "未提供"}</dd></div>
          <div><dt>Message-ID</dt><dd>{parsed.email.messageId || "未提供"}</dd></div>
          <div><dt>原文大小</dt><dd>{sizeText(parsed.rawSize)}</dd></div>
        </dl></details>}
      </div>
      {readError && <p className="reader-error" role="alert">{readError}</p>}
      {actionNotice && <p className="mail-action-notice" role="status">{actionNotice}</p>}
      {message.contentStatus !== "stored" ? <div className="mail-unavailable"><Inbox aria-hidden="true" /><h3>{message.contentStatus === "legacy" ? "这封旧邮件尚未保存正文" : "这封邮件的原文保存失败"}</h3><p>转发记录已保留。请在 Gmail 中查看，或下载原始 EML 文件后导入，补回标题、正文和附件。</p><button className="button" onClick={onImport}><FileUp aria-hidden="true" />补入邮件原文</button></div> : loading ? <div className="mail-unavailable" role="status"><RefreshCw className="spinning" aria-hidden="true" />正在读取完整邮件…</div> : error ? <div className="mail-unavailable" role="alert"><p>{error}</p><button className="button" onClick={() => setRetry(value => value + 1)}>重新读取</button></div> : parsed && <>
        <div className="mail-body-toolbar">
          <div className="body-mode" role="group" aria-label="正文显示方式"><button aria-pressed={!plain} disabled={!parsed.email.html} onClick={() => { cancelTranslation(); setPlain(false); }}>原始排版</button><button aria-pressed={plain} onClick={() => { cancelTranslation(); setPlain(true); }}>纯文本</button></div>
          <div className="mail-translate-controls"><label>原文语言 <select aria-label="原文语言" value={sourceLanguage} disabled={translating} onChange={event => { sourceLanguageTouched.current = true; cancelTranslation(); setSourceLanguage(event.target.value); }}><option value="en">英语</option><option value="zh">中文</option><option value="ja">日语</option><option value="ko">韩语</option><option value="fr">法语</option><option value="de">德语</option><option value="es">西班牙语</option></select></label><button type="button" className="button" onClick={() => void translateVisible()}>{translating ? "取消翻译" : translation ? "查看原文" : "翻译成中文"}</button></div>
          {!plain && (imageStatus.total > 0 || !!safeDocument?.missingInlineImages) && <span>{imageStatus.pending ? "图片加载中 · " + (imageStatus.total - imageStatus.pending - imageStatus.failed) + "/" + imageStatus.total : imageStatus.failed ? imageStatus.failed + " 张图片原站加载失败" : "图片已完整加载"}{safeDocument?.missingInlineImages ? " · " + safeDocument.missingInlineImages + " 张内嵌图片缺失" : ""}</span>}
        </div>
        {translationStatus && <p className="mail-translation-status" role="status">{translationStatus}</p>}
        {plain ? <pre className="mail-plain">{showTranslation?.body || parsed.email.text || "此邮件没有纯文本版本，请切换到原始排版。"}</pre> : <iframe ref={bodyFrame} title="邮件正文" className="mail-html" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" referrerPolicy="no-referrer" srcDoc={showTranslation?.body || safeDocument?.document} />}
        {parsed.attachments.length > 0 && <section className="mail-attachments" aria-label="邮件附件"><h3><Paperclip aria-hidden="true" />附件 · {parsed.attachments.length}</h3>
          {parsed.attachments.map((attachment, index) => <a key={index} href={attachment.url} download={attachment.name}><Paperclip aria-hidden="true" /><span>{attachment.name}<small>{sizeText(attachment.size)}</small></span><Download aria-hidden="true" /></a>)}
        </section>}
      </>}
    </section>
  );
}

export function RecordReader({ message, onClose, onMessageChange }: {
  message: InboxMessage; onClose: () => void; onMessageChange: (message: InboxMessage) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [revision, setRevision] = useState(0);
  const [metadataError, setMetadataError] = useState("");
  useLayoutEffect(() => {
    const opener = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const element = dialog.current;
    element?.showModal();
    return () => {
      element?.close();
      document.body.style.overflow = overflow;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setMetadataError("");
    void getJson<InboxMessage>("/api/messages/" + message.id, controller.signal)
      .then(value => { if (!controller.signal.aborted) onMessageChange(value); })
      .catch(() => { if (!controller.signal.aborted) setMetadataError("邮件状态未能更新。请检查网络后重试。"); });
    return () => controller.abort();
  }, [message.id, revision, onMessageChange]);
  return (
    <dialog ref={dialog} className="record-reader-dialog" aria-labelledby="record-reader-title" onKeyDown={keepDialogFocus}
      onCancel={event => { event.preventDefault(); if (!importOpen) onClose(); }}>
      <div className="record-reader-heading"><h2 id="record-reader-title">邮件全文</h2><button className="icon-button" aria-label="关闭邮件全文" onClick={onClose}><X aria-hidden="true" /></button></div>
      {metadataError && <div className="reader-error" role="alert">{metadataError}<button className="button" onClick={() => setRevision(value => value + 1)}>重试</button></div>}
      <Reader message={message} onClose={onClose} onMessageChange={onMessageChange} onImport={() => setImportOpen(true)} />
      {importOpen && <ImportDialog recipient={message.recipient} onClose={() => setImportOpen(false)} onImported={() => setRevision(value => value + 1)} />}
    </dialog>
  );
}

export function InboxView({ refreshKey, onOverview }: { refreshKey: number; onOverview: () => void }) {
  const [boxes, setBoxes] = useState(EMPTY_BOXES);
  const [boxQuery, setBoxQuery] = useState("");
  const [boxOffset, setBoxOffset] = useState(0);
  const [boxError, setBoxError] = useState("");
  const [recipient, setRecipient] = useState("");
  const [query, setQuery] = useState("");
  const [unread, setUnread] = useState(false);
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("nonpromotion");
  const [view, setView] = useState("inbox");
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState<InboxResponse>({ records: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  const [boxVersion, setBoxVersion] = useState(0);
  const [selected, setSelected] = useState<InboxMessage | null>(null);
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const importTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => { setOffset(0); setBoxOffset(0); }, [refreshKey]);

  useEffect(() => refreshWhileVisible(async (signal, automatic) => {
    try {
      const params = new URLSearchParams({ query: boxQuery, view, offset: String(automatic ? 0 : boxOffset) });
      const data = await getJson<MailboxesResponse>("/api/mailboxes?" + params, signal);
      // Refresh the already loaded window, including its first page, without collapsing it.
      while (automatic && data.mailboxes.length < Math.min(boxOffset + 100, data.total) && !signal.aborted) {
        params.set("offset", String(data.mailboxes.length));
        const next = await getJson<MailboxesResponse>("/api/mailboxes?" + params, signal);
        if (!next.mailboxes.length) break;
        data.mailboxes.push(...next.mailboxes); data.total = next.total;
      }
      if (signal.aborted) return;
      setBoxes(old => ({ ...data, mailboxes: automatic || boxOffset === 0 ? data.mailboxes : [...new Map([...old.mailboxes, ...data.mailboxes].map(box => [box.recipient, box])).values()] }));
      setBoxError("");
    } catch (failure) { if (!signal.aborted) setBoxError((failure as Error).message); }
  }), [boxQuery, boxOffset, view, refreshKey, boxVersion, version]);
  useEffect(() => {
    setLoading(true); setError("");
    return refreshWhileVisible(async (signal, automatic) => {
      try {
        const params = new URLSearchParams({ recipient, query, unread: unread ? "1" : "0", status, category, view, offset: String(automatic ? 0 : offset), limit: "50" });
        const data = await getJson<InboxResponse>("/api/messages?" + params, signal);
        while (automatic && data.records.length < Math.min(offset + 50, data.total) && !signal.aborted) {
          params.set("offset", String(data.records.length));
          const next = await getJson<InboxResponse>("/api/messages?" + params, signal);
          if (!next.records.length) break;
          data.records.push(...next.records); data.total = next.total;
        }
        if (signal.aborted) return;
        setResult(old => automatic || offset === 0 ? data : { ...data, records: [...new Map([...old.records, ...data.records].map(record => [record.id, record])).values()] });
        setLoading(false); setError("");
      } catch (failure) { if (!signal.aborted) { setError((failure as Error).message); setLoading(false); } }
    }, 200);
  }, [recipient, query, unread, status, category, view, offset, version, refreshKey]);
  function chooseBox(value: string) { setRecipient(value); setOffset(0); setSelected(null); setFoldersOpen(false); }
  function changedRead(id: string, read: boolean) {
    setResult(old => unread && read
      ? { total: old.total - Number(old.records.some(item => item.id === id)), records: old.records.filter(item => item.id !== id) }
      : { ...old, records: old.records.map(item => item.id === id ? { ...item, isRead: read } : item) });
    setSelected(old => old?.id === id ? { ...old, isRead: read } : old);
    setBoxOffset(0); setBoxVersion(value => value + 1);
  }
  function changedMessage(message: InboxMessage) {
    if (selected?.isRead !== message.isRead) changedRead(message.id, message.isRead);
    setResult(old => ({ ...old, records: old.records.map(item => item.id === message.id ? message : item) }));
    setSelected(old => old?.id === message.id ? message : old);
    setVersion(value => value + 1);
  }
  async function open(message: InboxMessage) {
    setSelected(message);
    if (message.isRead) return;
    try {
      const response = await privateFetch("/api/messages/" + message.id + "/read?value=1", { method: "PUT" });
      if (!response.ok) throw new Error();
      changedRead(message.id, true);
    } catch { setError("邮件已打开，但标记已读失败，请在阅读区重试。"); }
  }
  function closeReader() {
    const id = selected?.id; setSelected(null);
    window.requestAnimationFrame(() => document.getElementById("mail-row-" + id)?.focus());
  }
  return (
    <div className="inbox-content">
      <div className={"mail-workspace" + (selected ? " has-selection" : "")}>
        <aside className={"mail-folders" + (foldersOpen ? " folders-open" : "")} aria-label="按收件地址分类">
          <div className="folder-heading"><h2>{recipient || "所有收件箱"}</h2><button className="icon-button folder-toggle" aria-expanded={foldersOpen} aria-controls="folder-list" aria-label="展开或收起收件箱分类" onClick={() => setFoldersOpen(value => !value)}><ChevronDown aria-hidden="true" /></button></div>
          <div id="folder-list" className="folder-list">
            <button className={"folder-item" + (!recipient ? " selected" : "")} aria-pressed={!recipient} onClick={() => chooseBox("")}><Inbox aria-hidden="true" /><span>全部邮箱</span><b>{boxes.messages}</b></button>
            <label className="folder-search"><Search aria-hidden="true" /><span className="sr-only">查找收件箱</span><input value={boxQuery} onChange={e => { setBoxQuery(e.target.value); setBoxOffset(0); }} placeholder="查找收件地址" /></label>
            {boxes.mailboxes.map(box => <button key={box.recipient} title={box.recipient} className={"folder-item" + (recipient === box.recipient ? " selected" : "")} aria-pressed={recipient === box.recipient} onClick={() => chooseBox(box.recipient)}>
              <AtSign aria-hidden="true" /><span>{box.recipient}<small>{box.unread} 封未读</small></span><b>{box.count}</b>
            </button>)}
            {boxError && <p className="reader-error" role="alert">{boxError}<button className="button" onClick={() => setBoxVersion(value => value + 1)}>重试</button></p>}
            {!boxError && boxes.mailboxes.length === 0 && <p className="folder-empty">{boxQuery ? "没有匹配的收件箱" : "收到邮件后自动创建收件箱"}</p>}
            {boxes.mailboxes.length < boxes.total && <button className="button" onClick={() => setBoxOffset(boxes.mailboxes.length)}>更多收件箱</button>}
          </div>
        </aside>
        <section className="mail-list" aria-label="邮件列表">
          <h2 id="mail-list-heading" className="sr-only" tabIndex={-1}>邮件列表</h2>
          <div className="mail-list-toolbar">
            <div className="search-field"><Search aria-hidden="true" /><input aria-label="搜索邮件标题、发件人或收件地址" placeholder="搜索标题、发件人或收件地址" value={query} onChange={e => { setQuery(e.target.value); setOffset(0); setSelected(null); }} />{query && <button className="icon-button" aria-label="清除邮件搜索" onClick={() => { setQuery(""); setOffset(0); }}><X aria-hidden="true" /></button>}</div>
            <div className="mail-list-filters"><button className={"button" + (unread ? " unread-active" : "")} aria-pressed={unread} onClick={() => { setUnread(value => !value); setOffset(0); setSelected(null); }}>仅未读</button>
              <select aria-label="邮件分类" value={view} onChange={e => { setView(e.target.value); setOffset(0); setBoxOffset(0); setSelected(null); }}><option value="inbox">收件箱</option><option value="starred">星标</option><option value="pinned">置顶</option><option value="important">重要</option><option value="archived">归档</option><option value="trash">回收站</option></select>
              <select aria-label="邮件类型" value={category} onChange={e => { setCategory(e.target.value); setOffset(0); setSelected(null); setResult({ records: [], total: 0 }); setLoading(true); }}><option value="nonpromotion">非广告</option><option value="attention">需关注</option><option value="normal">普通</option><option value="promotion">广告</option><option value="all">全部类型（含广告）</option></select>
              <select aria-label="邮件转发状态" value={status} onChange={e => { setStatus(e.target.value); setOffset(0); setSelected(null); }}><option value="all">全部状态</option><option value="forwarded">已转发</option><option value="failed">转发失败</option><option value="processing">处理中</option><option value="imported">历史导入</option></select>
              <span role="status" title="页面可见时每 10 秒自动刷新">{loading ? "更新中…" : result.total + " 封 · 自动刷新"}</span>
              <details className="inbox-more"><summary>更多</summary><div>
                <button ref={importTrigger} className="button" onClick={() => setImportOpen(true)}><FileUp aria-hidden="true" />导入历史邮件</button>
                <button className="button" onClick={onOverview}>转发统计</button>
              </div></details>
            </div>
          </div>
          {error && <div className="reader-error" role="alert">{error}<button className="button" onClick={() => setVersion(value => value + 1)}>重试</button></div>}
          <div className="mail-rows" aria-busy={loading}>
            {result.records.map(message => <button id={"mail-row-" + message.id} key={message.id} className={"mail-row" + (selected?.id === message.id ? " selected" : "") + (message.isRead ? "" : " is-unread")} onClick={() => void open(message)} aria-pressed={selected?.id === message.id}>
              <span className="mail-row-party mail-row-from" title={message.senderName ? message.senderName + " <" + message.senderAddress + ">" : message.senderAddress}>
                <small>发件人</small><span className="mail-row-person">{message.senderName || message.senderAddress}</span>{message.senderName && <span className="mail-row-email">{message.senderAddress}</span>}
              </span>
              <span className="mail-row-party mail-row-to" title={message.recipientName ? message.recipientName + " <" + message.recipient + ">" : message.recipient}>
                <small>收件人</small><span className="mail-row-person">{message.recipientName || message.recipient}</span>{message.recipientName && <span className="mail-row-email">{message.recipient}</span>}
              </span>
              <span className="mail-row-subject" title={messageTitle(message)}><small>标题</small><strong><BilingualTitle title={messageTitle(message)} /></strong></span>
              <span className="mail-row-meta"><span className="mail-row-marks">{message.isStarred && <Star aria-label="星标" />}{message.isPinned && <Pin aria-label="置顶" />}{message.isImportant && <Flag aria-label="重要" />}</span><span className={"mail-category mail-category--" + message.category} title={message.categorySource === "manual" ? "手动分类" : message.categorySource === "important" ? "因重要标记归为需关注" : "根据主题与邮件头自动分类，可在邮件操作中修正"}>{categoryLabel[message.category]}</span><time dateTime={message.receivedAt}>{new Date(message.receivedAt).toLocaleDateString("zh-CN", { month: "2-digit", day: "2-digit" })}</time></span>
            </button>)}
            {!loading && !error && result.records.length === 0 && <div className="mail-unavailable"><Inbox aria-hidden="true" /><h3>{query || recipient || unread || status !== "all" || category !== "nonpromotion" || view !== "inbox" ? "没有符合条件的邮件" : "还没有非广告邮件"}</h3><p>发送到任意 @zzjmail.com 地址的邮件，会按收件地址归入这里。</p>{(query || recipient || unread || status !== "all" || category !== "nonpromotion" || view !== "inbox") && <button className="button" onClick={() => { setQuery(""); setRecipient(""); setUnread(false); setStatus("all"); setCategory("nonpromotion"); setView("inbox"); setOffset(0); }}>查看收件箱</button>}</div>}
            {loading && !result.records.length && <div className="mail-unavailable" role="status"><RefreshCw className="spinning" aria-hidden="true" />正在加载邮件…</div>}
            {result.records.length < result.total && <button className="button load-mail" disabled={loading} onClick={() => setOffset(result.records.length)}>加载更多邮件</button>}
          </div>
        </section>
        {selected && <Reader key={selected.id} message={selected} onClose={closeReader} onMessageChange={changedMessage} onImport={() => setImportOpen(true)} onSearch={value => { setQuery(value.slice(0, 200)); setOffset(0); setSelected(null); }} />}
      </div>
      {importOpen && <ImportDialog recipient={selected?.recipient || recipient} onClose={() => { setImportOpen(false); window.requestAnimationFrame(() => importTrigger.current?.focus()); }} onImported={() => { setOffset(0); setBoxOffset(0); setSelected(null); setVersion(value => value + 1); }} />}
    </div>
  );
}
